import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Brain, Bell, BookOpen, Pill,
  ChevronRight, Loader2, Sparkles, Clock, AlertCircle, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import GlobalFooter from '../components/GlobalFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Time-of-day color helper (matches RemindersPage).
 */
function todColor(time) {
  const h = parseInt((time || '08:00').split(':')[0], 10);
  if (h >= 5 && h < 11) return { dot: 'bg-amber-500', label: 'Morning' };
  if (h >= 11 && h < 17) return { dot: 'bg-sky-500', label: 'Midday' };
  if (h >= 17 && h < 21) return { dot: 'bg-orange-500', label: 'Evening' };
  return { dot: 'bg-indigo-500', label: 'Night' };
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MyPlanPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?mode=login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchAll = async () => {
      try {
        const [aRes, rRes, jRes] = await Promise.all([
          axios.get(`${API}/assessments`, { headers: getAuthHeader() }),
          axios.get(`${API}/reminders`, { headers: getAuthHeader() }),
          axios.get(`${API}/journal`, { headers: getAuthHeader() }),
        ]);
        const list = aRes.data.assessments || aRes.data || [];
        setAssessment(Array.isArray(list) && list.length > 0 ? list[0] : null);
        setReminders(rRes.data || []);
        setJournalEntries((jRes.data?.entries || jRes.data || []).slice(0, 5));
      } catch (e) {
        console.error('MyPlan load failed:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // No assessment yet — should not normally happen if hero gates this
  if (!assessment) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <header className="border-b border-border bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Logo />
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </div>
        </header>
        <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <Sparkles className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-3">No plan yet</h1>
          <p className="text-muted-foreground mb-6">Take the free assessment to get your personalized supplement plan.</p>
          <Button className="btn-primary" onClick={() => navigate('/quiz')}>
            Start Assessment <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </main>
        <GlobalFooter />
      </div>
    );
  }

  // Build "Your Supplements" from active reminders — this is the user's REAL plan,
  // not just whatever the latest quiz recommended in isolation. Dedupe by normalized
  // name, keep medication flag if any duplicate is flagged Rx. Collapse Water (Cup N)
  // entries into a single "💧 Water" tile with a count.
  const activeReminders = reminders.filter(r => r.enabled !== false);
  const stackMap = new Map(); // key -> { name, dosage, is_medication, count }
  activeReminders.forEach(r => {
    const rawName = r.supplement_name || '';
    const isWater = rawName.startsWith('💧') || /water \(cup/i.test(rawName);
    const displayName = isWater ? '💧 Water' : rawName.trim();
    const key = displayName.toLowerCase().replace(/\s+/g, ' ');
    if (!stackMap.has(key)) {
      stackMap.set(key, {
        name: displayName,
        dosage: r.dosage || '',
        is_medication: !!r.is_medication,
        count: 1,
      });
    } else {
      const entry = stackMap.get(key);
      entry.count += 1;
      if (r.is_medication) entry.is_medication = true;
      if (!entry.dosage && r.dosage) entry.dosage = r.dosage;
    }
  });
  const stack = [...stackMap.values()];
  const meds = stack.filter(s => s.is_medication);
  const nonMeds = stack.filter(s => !s.is_medication);
  const topSupps = stack.slice(0, 12);  // show meds + non-meds together

  // Reminder time groups
  const remindersByTime = {};
  activeReminders.forEach(r => {
    if (!remindersByTime[r.time]) remindersByTime[r.time] = [];
    remindersByTime[r.time].push(r);
  });
  const reminderTimes = Object.keys(remindersByTime).sort();

  const daysSince = Math.floor((Date.now() - new Date(assessment.created_at).getTime()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="my-plan-page">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <Logo />
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/quiz?prefill=true')}
            data-testid="my-plan-update-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Update
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-8">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <p className="text-xs uppercase tracking-wider text-primary/70 font-medium mb-1.5">Your Plan</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-2">
            Hi {user?.name?.split(' ')[0]}, here's your wellness plan
          </h1>
          <p className="text-muted-foreground">
            {stack.length} item{stack.length !== 1 ? 's' : ''} in your stack
            {meds.length > 0 && <> · <span className="font-semibold text-red-600">{meds.length} Rx</span></>}
            {' · '}Last assessment {formatDate(assessment.created_at)} ({daysSince}d ago)
          </p>
        </motion.div>

        {/* TOP: Supplements section */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-8"
          data-testid="my-plan-supplements"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" /> Your Supplements
            </h2>
            <button
              onClick={() => navigate('/reminders')}
              className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
            >
              Manage stack <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {meds.length > 0 && (
            <div className="mb-3 p-3 rounded-xl bg-red-50 border border-red-100 flex items-start gap-3" data-testid="my-plan-meds-warning">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-red-700 leading-relaxed">
                <span className="font-semibold">{meds.length} medication{meds.length !== 1 ? 's' : ''}</span> in your plan — always take Rx items as prescribed by your doctor.
              </p>
            </div>
          )}
          {stack.length === 0 ? (
            <div className="p-6 bg-white rounded-xl border border-border/50 text-center">
              <Pill className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-3">No active supplements yet. Set up reminders for your plan to see them here.</p>
              <Button size="sm" className="btn-primary" onClick={() => navigate('/reminders')}>
                Build your stack
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {topSupps.map((s, i) => (
                <div
                  key={i}
                  className={`p-4 bg-white rounded-xl border hover:shadow-sm transition-all ${
                    s.is_medication ? 'border-red-200' : 'border-border/50'
                  }`}
                  data-testid={`my-plan-supp-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      s.is_medication ? 'bg-red-100' : 'bg-primary/10'
                    }`}>
                      <Pill className={`w-4 h-4 ${s.is_medication ? 'text-red-600' : 'text-primary'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm font-semibold text-[#1A1A1A] truncate">{s.name}</p>
                        {s.is_medication && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 flex-shrink-0">Rx</span>
                        )}
                        {s.count > 1 && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">×{s.count}</span>
                        )}
                      </div>
                      {s.dosage && <p className="text-xs text-muted-foreground mt-0.5 truncate">{s.dosage}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {stack.length > 12 && (
            <button
              onClick={() => navigate('/reminders')}
              className="mt-3 text-sm text-primary font-medium hover:underline"
            >
              + {stack.length - 12} more in your stack
            </button>
          )}
        </motion.section>

        {/* MIDDLE: Today's Reminders */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
          data-testid="my-plan-reminders"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" /> Today's Schedule
            </h2>
            <button
              onClick={() => navigate('/reminders')}
              className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
            >
              Manage <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {reminderTimes.length === 0 ? (
            <div className="p-6 bg-white rounded-xl border border-border/50 text-center">
              <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-3">No reminders set yet.</p>
              <Button size="sm" className="btn-primary" onClick={() => navigate('/reminders')}>
                Set up reminders
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {reminderTimes.slice(0, 6).map(time => {
                const tod = todColor(time);
                const items = remindersByTime[time];
                const hasMed = items.some(r => r.is_medication);
                return (
                  <div key={time} className="p-4 bg-white rounded-xl border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-2 h-2 rounded-full ${tod.dot}`} />
                      <span className="text-sm font-semibold text-[#1A1A1A]">{time}</span>
                      <span className="text-xs text-muted-foreground">· {items.length} item{items.length !== 1 ? 's' : ''}</span>
                      {hasMed && (
                        <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">Rx</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      {items.map(r => r.supplement_name).join(', ')}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </motion.section>

        {/* BOTTOM: Recent Journal */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-8"
          data-testid="my-plan-journal"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" /> Recent Journal
            </h2>
            <button
              onClick={() => navigate('/journal')}
              className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
            >
              All entries <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {journalEntries.length === 0 ? (
            <div className="p-6 bg-white rounded-xl border border-border/50 text-center">
              <BookOpen className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground mb-3">No journal entries yet. Track how you feel over time.</p>
              <Button size="sm" className="btn-primary" onClick={() => navigate('/journal')}>
                Log your first entry
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {journalEntries.map((j, i) => (
                <div key={i} className="p-3 bg-white rounded-xl border border-border/50 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A1A1A]">{formatDate(j.date || j.created_at)}</p>
                    {(j.notes || j.entry) && <p className="text-xs text-muted-foreground truncate">{j.notes || j.entry}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.section>
      </main>

      <GlobalFooter />
    </div>
  );
}
